import { vueAppStore } from '@amzn/AmazonJourneysVueApp';
import type { PageOfPodsInDocLoadedEvent } from '@amzn/AmazonJourneysVueApp/interop/components/project/store/project.events';
import { Button, Menu, MenuItem } from '@amzn/meridian';
import { namespaceEventType } from 'app.util';
import { useJAlertsContext } from 'components/JAlerts/JAlertsContext';
import { LoadingOverlay } from 'components/LoadingOverlay';
import { LandingPagePreviewModal } from 'features/advanced-preview/LandingPagePreviewModal';
import { useCloneTrafficProjectMutation } from 'generated/graphql';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { filter as filter$ } from 'rxjs/operators';
import { TPRenameModal } from './TPRenameModal/TPRenameModal';

export const TPActionMenu = () => {
  const { trafficProjectId } = useParams<{
    trafficProjectId: string;
  }>();
  const buttonRef = useRef();
  const [publish, setPublish] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [openTPRenameModal, setOpenTPRenameModal] = useState(false);
  const [openPreviewModal, setOpenPreviewModal] = useState(false);
  const onClickTPRenameItem = useCallback(() => setOpenTPRenameModal(true), []);
  const onClickPreviewModal = useCallback(() => {
    setOpenPreviewModal(true);
    setPublish(true);
  }, []);
  const onClosePreviewModal = useCallback(() => setOpenPreviewModal(false), []);
  const onCloseTPRenameItem = useCallback(() => setOpenTPRenameModal(false), []);
  const onClickButton = useCallback(() => {
    setOpen(true);
  }, []);
  const onClose = useCallback(() => setOpen(false), []);

  const history = useHistory();
  const [cloneTrafficProject, { data: cloneProjectData, loading, error: cloneProjectError }] = useCloneTrafficProjectMutation();
  const { addAlert } = useJAlertsContext();
  const handleCloneProjectClick = async () => {
    await cloneTrafficProject({
      variables: {
        input: {
          id: trafficProjectId,
        },
      },
    }).catch(() => {
      addAlert({
        alertType: 'error',
        message: 'There was a Clone error and the action could not be completed.',
        title: 'Error:',
      });
    });
  };
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy code
  useEffect(() => {
    if (!loading && !cloneProjectError && cloneProjectData?.cloneTrafficProject) {
      history.push(`/traffic/projects/${cloneProjectData?.cloneTrafficProject?.project?.id}/campaigns`);
    }
  }, [
    loading,
    cloneProjectData,
    cloneProjectError,
  ]);

  useEffect(() => {
    if (!trafficProjectId) {
      return;
    }
    if (publish) {
      vueAppStore.publish({
        id: trafficProjectId,
        type: 'DocumentComponentCreated',
      });
      vueAppStore.publish({
        contentWidth: 0,
        id: trafficProjectId,
        type: namespaceEventType('project', 'ProjectComponentCreated'),
      });
    }
    // @ts-expect-error
    vueAppStore.event$.pipe(filter$((evt) => evt.type === namespaceEventType('project', 'PageOfPodsInDocLoaded'))).subscribe((evt) => {
      const { nextToken } = evt as unknown as PageOfPodsInDocLoadedEvent;
      if (!nextToken) setIsLoading(false);
    });
  }, [
    publish,
    trafficProjectId,
  ]);

  return (
    <>
      {loading && <LoadingOverlay dataCy="loading spinner" visible />}
      {!loading && (
        <>
          <Button
            aria-controls="project-action-menu-button"
            aria-expanded="false"
            aria-haspopup="menu"
            data-cy="actions-button"
            onClick={onClickButton}
            ref={buttonRef}
            type="secondary"
          >
            Project actions
          </Button>
          <Menu anchorNode={buttonRef.current} id="project-action-menu-button" onClose={onClose} open={open} position="bottom">
            <MenuItem data-cy="rename-project-button" onClick={onClickTPRenameItem}>
              Rename
            </MenuItem>
            <MenuItem data-cy="clone-project-button" onClick={handleCloneProjectClick}>
              Clone
            </MenuItem>
            <MenuItem data-cy="preview-button" onClick={onClickPreviewModal}>
              Preview
            </MenuItem>
          </Menu>
          <TPRenameModal onCloseModal={onCloseTPRenameItem} openModal={openTPRenameModal} />
          <LandingPagePreviewModal
            isLandingPagePreview
            isLoading={isLoading}
            onCloseModal={onClosePreviewModal}
            openModal={openPreviewModal}
            trafficProjectId={trafficProjectId}
          />
        </>
      )}
    </>
  );
};
